import React, { useEffect, useState } from 'react';
import { IonToast } from '@ionic/react';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';

let checkForUpdatesCallback: (() => void) | null = null;

const UpdateNotification: React.FC = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [showNoUpdate, setShowNoUpdate] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [checking, setChecking] = useState(false);
  const justUpdatedRef = React.useRef(false);

  useEffect(() => {
    // Kontrollera om vi precis har laddat om från en uppdatering
    const wasUpdating = localStorage.getItem('updating-sw');
    if (wasUpdating) {
      console.log('Just reloaded after update, setting justUpdated flag and clearing storage');
      justUpdatedRef.current = true;
      localStorage.removeItem('updating-sw');
      
      // Rensa justUpdated-flaggan efter 5 sekunder
      setTimeout(() => {
        console.log('Clearing justUpdated flag');
        justUpdatedRef.current = false;
      }, 5000);
    }

    // Rensa gamla firebase-messaging-sw.js registreringar om de finns
    const cleanupOldServiceWorkers = async () => {
      if ('serviceWorker' in navigator) {
        // Kolla om vi redan har försökt cleanup för att undvika oändlig loop
        const lastCleanup = localStorage.getItem('sw-last-cleanup');
        const now = Date.now();
        if (lastCleanup && (now - parseInt(lastCleanup)) < 10000) {
          console.log('Cleanup attempted recently, skipping to avoid loop');
          return true;
        }

        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('Checking for old SW registrations to cleanup:', registrations.length);
        
        // Kolla om det finns en service-worker.js
        const hasServiceWorker = registrations.some(reg => {
          const url = reg.active?.scriptURL || reg.waiting?.scriptURL || reg.installing?.scriptURL || '';
          return url.includes('service-worker.js');
        });
        
        if (hasServiceWorker) {
          console.log('service-worker.js is registered, no cleanup needed');
          return true;
        }
        
        const oldRegistrations = [];
        for (const reg of registrations) {
          const scriptURL = reg.active?.scriptURL || reg.waiting?.scriptURL || reg.installing?.scriptURL || '';
          console.log('Checking registration:', scriptURL);
          // Om det är en direkt firebase-messaging-sw.js
          if (scriptURL.includes('firebase-messaging-sw.js')) {
            console.log('Found old firebase-messaging-sw.js registration to remove:', scriptURL);
            oldRegistrations.push(reg);
          }
        }
        
        if (oldRegistrations.length > 0) {
          console.log(`Unregistering ${oldRegistrations.length} old firebase-messaging-sw.js registrations...`);
          localStorage.setItem('sw-last-cleanup', now.toString());
          
          for (const reg of oldRegistrations) {
            try {
              const success = await reg.unregister();
              console.log('Unregister result:', success);
            } catch (err) {
              console.error('Error unregistering:', err);
            }
          }
          console.log('Old SW unregistered, reloading page to register service-worker.js...');
          // Ladda om sidan för att registrera den nya service-worker.js
          setTimeout(() => {
            window.location.reload();
          }, 500);
          return false; // Indikera att vi laddar om
        }
      }
      return true; // Fortsätt med normal registrering
    };

    // Lyssna på service worker uppdateringar
    const handleUpdate = (reg: ServiceWorkerRegistration) => {
      console.log('onUpdate callback triggered, has waiting:', !!reg.waiting, 'justUpdated:', justUpdatedRef.current);
      
      // Ignorera om vi just har uppdaterat
      if (justUpdatedRef.current) {
        console.log('Ignoring update notification - just reloaded from update');
        return;
      }
      
      // Kolla om vi redan visar uppdatering eller om detta är en riktig ny uppdatering
      if (reg.waiting) {
        setRegistration(reg);
        setShowUpdate(true);
      }
    };

    // Exportera callback för service worker registrering från index.tsx
    (window as any).__SW_UPDATE_CALLBACK__ = handleUpdate;
    console.log('SW update callback registered');

    // Kör cleanup efter en liten fördröjning
    setTimeout(async () => {
      await cleanupOldServiceWorkers();
    }, 1000);

    // Kolla efter uppdateringar var 30:e minut
    const intervalId = setInterval(() => {
      checkForUpdates(false);
    }, 30 * 60 * 1000); // 30 minuter

    // Exponera funktionen för manuell kontroll
    checkForUpdatesCallback = () => checkForUpdates(true);

    return () => {
      clearInterval(intervalId);
      checkForUpdatesCallback = null;
    };
  }, []);

  const checkForUpdates = async (isManual: boolean = false) => {
    console.log('checkForUpdates called with isManual:', isManual);
    
    if (checking) {
      console.log('Already checking, skipping...');
      return;
    }
    
    setChecking(true);
    console.log('Starting update check...');
    
    // Stäng alla tidigare meddelanden
    setShowUpdate(false);
    setShowNoUpdate(false);
    
    try {
      console.log('Checking if serviceWorker is available:', 'serviceWorker' in navigator);
      console.log('User agent:', navigator.userAgent);
      console.log('Current location:', window.location.href);
      
      // Kontrollera om service worker överhuvudtaget är registrerad
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('Total SW registrations:', registrations.length);
        registrations.forEach((reg, index) => {
          console.log(`Registration ${index}:`, reg.active?.scriptURL || 'no active SW');
        });
        
        if (registrations.length === 0) {
          console.error('ERROR: No service workers registered!');
          setChecking(false);
          setShowNoUpdate(true);
          return;
        }
      }
      
      const reg = await navigator.serviceWorker.getRegistration();
      console.log('Service Worker registration:', reg);
      console.log('Active SW:', reg?.active?.scriptURL);
      console.log('Waiting SW before update:', reg?.waiting?.scriptURL);
      console.log('Installing SW before update:', reg?.installing?.scriptURL);
      
      if (reg) {
        console.log('Kollar efter uppdateringar...');
        
        // Kolla om det redan finns en waiting service worker innan vi uppdaterar
        const hadWaiting = !!reg.waiting;
        console.log('Had waiting SW before update:', hadWaiting);
        
        // Forcera uppdatering genom att lägga till cache-busting parameter
        console.log('Calling reg.update()...');
        await reg.update();
        console.log('Update check completed');
        
        // Vänta lite för att service worker ska kunna installera och vänta
        // Längre väntetid för Android
        const waitTime = /android/i.test(navigator.userAgent) ? 5000 : 3000;
        console.log(`Waiting ${waitTime}ms for SW to install...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        
        // Hämta ALLA service worker registreringar
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('All SW registrations:', registrations.map(r => ({
          scope: r.scope,
          active: r.active?.scriptURL,
          waiting: r.waiting?.scriptURL,
          installing: r.installing?.scriptURL
        })));
        
        // Kolla om NÅGON registrering har en väntande uppdatering
        const regWithUpdate = registrations.find(r => r.waiting);
        const hasAnyWaiting = !!regWithUpdate;
        
        console.log('Found registration with waiting SW:', regWithUpdate?.active?.scriptURL);
        console.log('Has any waiting SW:', hasAnyWaiting);
        
        // Hämta registrering igen för att se aktuell status
        const updatedReg = await navigator.serviceWorker.getRegistration();
        const hasWaiting = !!(updatedReg?.waiting);
        console.log('Active SW after update:', updatedReg?.active?.scriptURL);
        console.log('Waiting SW after update:', updatedReg?.waiting?.scriptURL);
        console.log('Installing SW after update:', updatedReg?.installing?.scriptURL);
        console.log('Has waiting SW after update:', hasWaiting, 'showUpdate:', showUpdate);
        
        // Kolla även service-worker.js specifikt (inte firebase-messaging-sw.js)
        const serviceWorkerReg = registrations.find(r => 
          r.active?.scriptURL.includes('service-worker.js')
        );
        const hasServiceWorkerUpdate = !!(serviceWorkerReg?.waiting);
        console.log('Service worker reg has update:', hasServiceWorkerUpdate);
        
        if (isManual) {
          // Debug info för Android
          const debugInfo = `
Active: ${updatedReg?.active?.scriptURL || 'none'}
Waiting: ${updatedReg?.waiting?.scriptURL || 'none'}
Installing: ${updatedReg?.installing?.scriptURL || 'none'}
Has waiting: ${hasWaiting}
Has any waiting: ${hasAnyWaiting}
Has SW update: ${hasServiceWorkerUpdate}
ShowUpdate: ${showUpdate}
          `.trim();
          console.log('DEBUG INFO:', debugInfo);
          
          // Om det finns en waiting service worker i NÅGON registrering
          if (hasAnyWaiting || hasWaiting || hasServiceWorkerUpdate || showUpdate) {
            console.log('Update detected, showing update toast');
            // Använd registreringen som har waiting SW
            if (regWithUpdate) {
              setRegistration(regWithUpdate);
            } else if (serviceWorkerReg?.waiting) {
              setRegistration(serviceWorkerReg);
            } else if (updatedReg?.waiting) {
              setRegistration(updatedReg);
            }
            setShowUpdate(true);
          } else {
            console.log('No update found, showing no-update message');
            setShowNoUpdate(true);
          }
        }
      } else {
        console.log('No service worker registration found');
        if (isManual) {
          alert('Service Worker är inte registrerad än.');
        }
      }
    } catch (error) {
      console.error('Fel vid uppdateringskontroll:', error);
      if (isManual) {
        setShowNoUpdate(true);
      }
    } finally {
      console.log('checkForUpdates finished');
      setChecking(false);
    }
  };

  const handleReload = () => {
    console.log('handleReload called, registration:', registration);
    setShowUpdate(false); // Stäng toasten direkt
    
    // Markera i localStorage att vi håller på att uppdatera
    localStorage.setItem('updating-sw', 'true');
    
    if (registration && registration.waiting) {
      // Skicka meddelande till service worker att aktivera den nya versionen
      console.log('Sending SKIP_WAITING message to waiting service worker');
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Lyssna på när den nya service worker har tagit över
      let reloaded = false;
      const handleControllerChange = () => {
        if (!reloaded) {
          reloaded = true;
          console.log('Controller changed, reloading...');
          localStorage.removeItem('updating-sw');
          window.location.reload();
        }
      };
      
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
      
      // Säkerhetsnät: ladda om efter 2 sekunder även om controllerchange inte triggas
      setTimeout(() => {
        if (!reloaded) {
          reloaded = true;
          console.log('Timeout reached, forcing reload...');
          localStorage.removeItem('updating-sw');
          window.location.reload();
        }
      }, 2000);
    } else {
      // Om ingen waiting SW finns, bara ladda om
      console.log('No waiting SW, just reloading...');
      localStorage.removeItem('updating-sw');
      window.location.reload();
    }
  };

  return (
    <>
      <IonToast
        isOpen={showUpdate}
        message="En ny version finns tillgänglig!"
        position="top"
        buttons={[
          {
            text: 'Ladda om',
            role: 'info',
            handler: handleReload
          },
          {
            text: 'Senare',
            role: 'cancel',
            handler: () => setShowUpdate(false)
          }
        ]}
        duration={0}
        color="primary"
      />
      <IonToast
        isOpen={showNoUpdate}
        message="Du har redan den senaste versionen!"
        position="top"
        duration={2000}
        color="success"
        onDidDismiss={() => setShowNoUpdate(false)}
      />
    </>
  );
};

// Exportera funktionen för manuell kontroll
export const manualCheckForUpdates = () => {
  console.log('manualCheckForUpdates called, callback exists:', !!checkForUpdatesCallback);
  
  if (checkForUpdatesCallback) {
    checkForUpdatesCallback();
  } else {
    console.warn('Service Worker not ready yet, callback not available');
  }
};

export default UpdateNotification;
